import React, { useEffect, useRef } from 'react';
import { setTurnstileTokenProvider } from '../../api/http';

const TURNSTILE_SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const TOKEN_TIMEOUT_MS = 60000;
const SCRIPT_LOAD_TIMEOUT_MS = 10000;

type TurnstileWidgetId = string;

type TurnstileApi = {
  render: (
    container: HTMLElement,
    options: {
      sitekey: string;
      size?: 'normal' | 'flexible' | 'compact';
      execution?: 'render' | 'execute';
      appearance?: 'always' | 'execute' | 'interaction-only';
      callback?: (token: string) => void;
      'error-callback'?: (errorCode?: string) => void;
      'expired-callback'?: () => void;
    }
  ) => TurnstileWidgetId;
  remove: (widgetId: TurnstileWidgetId) => void;
};

declare global {
  interface Window {
    turnstile?: TurnstileApi;
    __APP_CONFIG__?: {
      TURNSTILE_SITE_KEY?: string;
    };
  }
}

let scriptPromise: Promise<void> | null = null;
let isProviderConfigured = false;

const getSiteKey = () =>
  window.__APP_CONFIG__?.TURNSTILE_SITE_KEY || process.env.REACT_APP_TURNSTILE_SITE_KEY || '';

const loadTurnstileScript = () => {
  if (window.turnstile) {
    return Promise.resolve();
  }

  if (scriptPromise) {
    return scriptPromise;
  }

  scriptPromise = new Promise<void>((resolve, reject) => {
    const existingScript = document.querySelector<HTMLScriptElement>('script[data-turnstile-script="true"]');
    if (existingScript) {
      existingScript.addEventListener('load', () => resolve(), { once: true });
      existingScript.addEventListener('error', () => reject(new Error('No se ha podido cargar Turnstile.')), { once: true });
      return;
    }

    const timeoutId = window.setTimeout(() => reject(new Error('Turnstile ha tardado demasiado en cargar.')), SCRIPT_LOAD_TIMEOUT_MS);
    const script = document.createElement('script');
    script.src = TURNSTILE_SCRIPT_URL;
    script.async = true;
    script.defer = true;
    script.dataset.turnstileScript = 'true';
    script.onload = () => {
      window.clearTimeout(timeoutId);
      resolve();
    };
    script.onerror = () => {
      window.clearTimeout(timeoutId);
      reject(new Error('No se ha podido cargar Turnstile.'));
    };
    document.head.appendChild(script);
  });

  return scriptPromise;
};

const generateTurnstileToken = async (container: HTMLDivElement | null): Promise<string> => {
  const siteKey = getSiteKey();
  if (!siteKey) {
    throw new Error('TURNSTILE_SITE_KEY no esta configurado.');
  }

  if (!container) {
    throw new Error('No se ha podido inicializar Turnstile.');
  }

  await loadTurnstileScript();

  if (!window.turnstile) {
    throw new Error('Turnstile no esta disponible.');
  }

  const turnstile = window.turnstile;

  return new Promise<string>((resolve, reject) => {
    let widgetId: TurnstileWidgetId | null = null;
    const cleanup = () => {
      window.clearTimeout(timeoutId);
      if (widgetId) {
        turnstile.remove(widgetId);
      }
    };
    const timeoutId = window.setTimeout(() => {
      cleanup();
      reject(new Error('Turnstile no ha devuelto token.'));
    }, TOKEN_TIMEOUT_MS);

    widgetId = turnstile.render(container, {
      sitekey: siteKey,
      size: 'normal',
      execution: 'render',
      appearance: 'always',
      callback: (token) => {
        cleanup();
        resolve(token);
      },
      'error-callback': (errorCode) => {
        cleanup();
        const details = errorCode ? ` (codigo ${errorCode})` : '';
        reject(new Error(`Turnstile no ha podido verificar la peticion${details}.`));
      },
      'expired-callback': () => {
        cleanup();
        reject(new Error('El token de Turnstile ha caducado.'));
      },
    });
  });
};

export function TurnstileInitializer({ children }: { children: React.ReactNode }) {
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isProviderConfigured) {
      setTurnstileTokenProvider(() => generateTurnstileToken(containerRef.current));
      isProviderConfigured = true;
    }
  }, []);

  return (
    <>
      {children}
      <div ref={containerRef} className="turnstile-container" />
    </>
  );
}
